<template>
  <v-dialog
    :model-value="modelValue"
    max-width="720px"
    persistent
    scrollable
    data-testid="cfgv2-qimport-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title class="bg-primary text-white d-flex align-center pa-4">
        <v-icon class="mr-2">mdi-import</v-icon>
        <div class="flex-grow-1">
          <div class="text-h6">นำเข้าชุดคำถามจากไฟล์</div>
          <div v-if="configVersionLabel" class="text-caption text-white-50">
            นำเข้าลงคอนฟิก {{ configVersionLabel }}
          </div>
        </div>
        <v-btn
          icon
          variant="text"
          color="white"
          data-testid="cfgv2-qimport-close-btn"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="comfortable"
          class="mb-4"
          data-testid="cfgv2-qimport-error"
        >
          {{ error }}
        </v-alert>

        <v-alert
          type="info"
          variant="tonal"
          density="comfortable"
          class="mb-4"
          data-testid="cfgv2-qimport-hint"
        >
          ไฟล์นี้จะถูกสร้างเป็น <strong>ชุดคำถามใหม่</strong> ใต้คอนฟิกนี้เสมอ และเป็น draft (ยังไม่ active) —
          ชุดคำถามที่ใช้งานอยู่จะไม่ถูกแตะ
        </v-alert>

        <!-- ── 1. เลือกไฟล์ / วาง JSON ─────────────────────────────── -->
        <div class="d-flex align-center flex-wrap ga-2">
          <v-btn
            variant="outlined"
            size="small"
            prepend-icon="mdi-file-upload-outline"
            data-testid="cfgv2-qimport-pick"
            @click="pickImportFile"
          >
            เลือกไฟล์ .json
          </v-btn>
          <span v-if="fileName" class="text-caption text-medium-emphasis" data-testid="cfgv2-qimport-filename">
            {{ fileName }}
          </span>
        </div>
        <input
          ref="importFile"
          type="file"
          accept="application/json,.json"
          class="d-none"
          data-testid="cfgv2-qimport-file"
          @change="onImportFileChosen"
        >

        <v-textarea
          v-model="importText"
          class="mt-3"
          label="หรือวาง JSON ที่นี่"
          rows="6"
          variant="outlined"
          density="compact"
          hide-details
          data-testid="cfgv2-qimport-text"
          @update:model-value="onTextInput"
        />

        <v-alert
          v-if="parseError"
          type="error"
          variant="tonal"
          density="comfortable"
          class="mt-3"
          data-testid="cfgv2-qimport-parse-error"
        >
          {{ parseError }}
        </v-alert>

        <!-- ที่มาของไฟล์ (อ่านจากตัวไฟล์เอง — ยังไม่ได้ยิง dry-run) -->
        <v-alert
          v-else-if="sourceLines.length"
          type="info"
          variant="tonal"
          density="comfortable"
          class="mt-3"
          icon="mdi-file-document-outline"
          data-testid="cfgv2-qimport-source"
        >
          <div class="text-subtitle-2 mb-1">ที่มาของไฟล์</div>
          <div v-for="line in sourceLines" :key="line.label" class="text-caption">
            {{ line.label }}: <strong>{{ line.value }}</strong>
          </div>
        </v-alert>

        <!-- ── 2. รหัสชุด / คำอธิบาย ────────────────────────────────── -->
        <v-row class="mt-2" dense>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="versionCode"
              label="รหัสชุดใหม่"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-tag-outline"
              placeholder="Q3"
              hint="เว้นว่าง = ใช้รหัสในไฟล์ · ถ้าชนของเดิมระบบจะต่อท้ายให้ (Q3 → Q3-2)"
              persistent-hint
              data-testid="cfgv2-qimport-code-input"
              @update:model-value="onCodeInput"
            />
          </v-col>
          <v-col cols="12" md="7">
            <v-text-field
              v-model="description"
              label="คำอธิบาย"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-text-box-outline"
              placeholder="รายละเอียดชุดคำถามนี้"
              hint="เว้นว่าง = ใช้คำอธิบายในไฟล์"
              persistent-hint
              data-testid="cfgv2-qimport-description-input"
              @update:model-value="descriptionTouched = true"
            />
          </v-col>
        </v-row>

        <!-- ── 3. ผลการตรวจไฟล์ (dry-run) ───────────────────────────── -->
        <v-card
          v-if="preview"
          variant="tonal"
          class="mt-4"
          data-testid="cfgv2-qimport-preview"
        >
          <v-card-text class="pa-4">
            <div class="text-subtitle-2 mb-2">ผลการตรวจไฟล์</div>

            <div class="d-flex align-center flex-wrap ga-2 mb-3">
              <v-chip size="small" variant="flat" color="primary" prepend-icon="mdi-tag">
                รหัสชุดที่จะได้: {{ preview.versionCode }}
              </v-chip>
              <v-chip size="small" variant="tonal" prepend-icon="mdi-format-list-numbered">
                {{ preview.stepCount }} Section
              </v-chip>
              <v-chip size="small" variant="tonal" prepend-icon="mdi-comment-question-outline">
                {{ preview.itemCount }} คำถาม
              </v-chip>
              <v-chip
                v-if="preview.droppedCalcFunctionCount > 0"
                size="small"
                variant="tonal"
                color="warning"
                prepend-icon="mdi-function-variant"
              >
                ตัดสูตร {{ preview.droppedCalcFunctionCount }} ตัว
              </v-chip>
              <v-chip
                v-if="preview.droppedCalcConfigCount > 0"
                size="small"
                variant="tonal"
                color="warning"
                prepend-icon="mdi-calculator-variant-outline"
              >
                ตัดการคำนวณ {{ preview.droppedCalcConfigCount }} ข้อ
              </v-chip>
            </div>

            <v-alert
              v-if="preview.versionCodeAdjusted"
              type="warning"
              variant="tonal"
              density="compact"
              class="mb-3"
              data-testid="cfgv2-qimport-code-adjusted"
            >
              รหัสที่ระบุชนกับชุดคำถามเดิมในคอนฟิกนี้ — ระบบเปลี่ยนให้เป็น
              <strong>{{ preview.versionCode }}</strong> อัตโนมัติ
            </v-alert>

            <v-alert
              v-if="previewWarnings.length"
              type="warning"
              variant="tonal"
              density="comfortable"
              data-testid="cfgv2-qimport-warnings"
            >
              <div class="text-subtitle-2 mb-1">
                พบข้อควรระวัง {{ previewWarnings.length }} รายการ — นำเข้าต่อได้ แต่ควรอ่านก่อน
              </div>
              <ul class="cfgv2-qimport-warn-list">
                <li v-for="(w, i) in previewWarnings" :key="i" class="text-caption">{{ w }}</li>
              </ul>
            </v-alert>
            <v-alert
              v-else
              type="success"
              variant="tonal"
              density="comfortable"
              data-testid="cfgv2-qimport-ok"
            >
              ตรวจไฟล์แล้ว ไม่พบปัญหา — นำเข้าได้เลย
            </v-alert>
          </v-card-text>
        </v-card>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn
          variant="outlined"
          :disabled="checking || importing"
          data-testid="cfgv2-qimport-cancel-btn"
          @click="close"
        >
          <v-icon start>mdi-close</v-icon>
          ยกเลิก
        </v-btn>
        <v-btn
          color="primary"
          variant="tonal"
          :disabled="!parsedBundle || importing"
          :loading="checking"
          data-testid="cfgv2-qimport-check-btn"
          @click="runDryRun"
        >
          <v-icon start>mdi-file-search-outline</v-icon>
          ตรวจไฟล์
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          :disabled="!preview || checking"
          :loading="importing"
          data-testid="cfgv2-qimport-submit-btn"
          @click="runImport"
        >
          <v-icon start>mdi-import</v-icon>
          นำเข้าเลย
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Import a Tax Question V2 bundle (`.json` produced by the row's «ส่งออกเป็นไฟล์») as a NEW
 * question version under ONE config version — the replacement for the hand-run python migrate
 * scripts (Local → Dev → UAT).
 *
 * Contract with the parent (`views/TaxConfigurationV2.vue`):
 *   - the parent owns the target identity (`configVersionId`) and the snackbar;
 *   - this dialog reports its own errors inline (`v-alert`), never through a snackbar of its own
 *     (same split as `components/TaxQuestionHistoryV2Dialog.vue`);
 *   - `imported` fires AFTER the real (non-dry-run) call succeeded, carrying the whole
 *     `ImportTaxQuestionBundleV2Result`, and this dialog does NOT close itself — the parent
 *     decides, because it is the one that must reload the list and surface the warnings.
 *     «ยกเลิก» / «X» still emit `update:modelValue: false` normally.
 *
 * Two-step by design: «ตรวจไฟล์» runs the SAME server-side remap with `dryRun: true` (writes
 * nothing) and its result is what unlocks «นำเข้าเลย». A missing `fieldKey` or formula never
 * blocks the import — it comes back in `warnings`, so it is shown, not swallowed: a silent
 * partial load is worse than a refusal.
 *
 * `configVersionId` (the row the Import button sits under) is ALWAYS the target — `bundle.source.*`
 * is untrusted display-only provenance and never resolves anything.
 */
import taxQuestionV2Api from '@/services/taxQuestionV2Api';

/** Envelope marker written by the export endpoint — sniffed before anything else is touched. */
const BUNDLE_KIND = 'lhb.taxquestion.v2.bundle';

export default {
  name: 'TaxQuestionImportV2Dialog',
  props: {
    modelValue: { type: Boolean, default: false },
    /** target config version — the new question set is created under THIS one */
    configVersionId: { type: Number, default: null },
    /** shown as the dialog subtitle (e.g. `2568 / V1`) */
    configVersionLabel: { type: String, default: '' },
    /** parent's suggestion for the new code, used until the file supplies its own */
    suggestedCode: { type: String, default: '' },
  },
  emits: ['update:modelValue', 'imported'],
  data() {
    return {
      importText: '',
      fileName: '',
      /** why the pasted/loaded text is not a usable bundle (set from `parsedBundle`) */
      parseError: '',
      /** API-level failure of the dry run / the import itself */
      error: '',
      /** `ImportTaxQuestionBundleV2Result` of the last dry run — gates «นำเข้าเลย» */
      preview: null,
      versionCode: '',
      description: '',
      /** stop the auto-prefill from the file once the user has typed their own */
      codeTouched: false,
      descriptionTouched: false,
      checking: false,
      importing: false,
    };
  },
  computed: {
    /**
     * The parsed bundle, or null while the text does not hold a usable one. Doubles as the
     * «ตรวจไฟล์» button's enable test — parsing here rather than on submit means a wrong file
     * (a Tax Config V1 export, a Playground snapshot) is caught before the server is called.
     * Sets `parseError` as a side effect so the reason is visible.
     * (Same shape as `views/TaxPlaygroundV2.vue` → `importPreview`.)
     */
    parsedBundle() {
      const raw = (this.importText || '').trim();
      if (!raw) { this.parseError = ''; return null; }
      let bundle;
      try {
        bundle = JSON.parse(raw);
      } catch (e) {
        this.parseError = 'ไฟล์นี้ไม่ใช่ JSON ที่อ่านได้';
        return null;
      }
      if (!bundle || typeof bundle !== 'object' || Array.isArray(bundle)) {
        this.parseError = 'ไฟล์นี้ไม่ใช่ไฟล์ชุดคำถาม Tax Question V2';
        return null;
      }
      if (bundle.kind !== BUNDLE_KIND) {
        this.parseError = bundle.kind
          ? `ไฟล์นี้ไม่ใช่ไฟล์ชุดคำถาม Tax Question V2 — ไฟล์นี้เป็น "${bundle.kind}"`
          : 'ไฟล์นี้ไม่ใช่ไฟล์ชุดคำถาม Tax Question V2 — ไม่พบ kind ในไฟล์';
        return null;
      }
      if (!Array.isArray(bundle.items) || bundle.items.length === 0) {
        this.parseError = 'ไฟล์นี้ไม่มีคำถามอยู่เลย (items ว่าง)';
        return null;
      }
      this.parseError = '';
      return bundle;
    },
    /** `source.*` of the loaded file — every null field is DROPPED, never rendered as "null". */
    sourceLines() {
      const b = this.parsedBundle;
      const src = (b && b.source) || null;
      if (!src) return [];
      const lines = [];
      const push = (label, value) => {
        if (value === null || value === undefined || String(value).trim() === '') return;
        lines.push({ label, value: String(value) });
      };
      push('ชุดคำถามต้นทาง', src.questionVersionCode);
      push('คอนฟิกต้นทาง', src.configVersionCode);
      push('ปีภาษี', src.taxYearCode);
      push('ปี พ.ศ.', src.beYear);
      push('คำอธิบาย', src.description);
      const steps = Array.isArray(b.steps) ? b.steps.length : 0;
      const items = Array.isArray(b.items) ? b.items.length : 0;
      lines.push({ label: 'ในไฟล์', value: `${steps} Section · ${items} คำถาม` });
      if (b.exportedAt) push('ส่งออกเมื่อ', b.exportedAt);
      if (b.exportedBy) push('ส่งออกโดย', b.exportedBy);
      return lines;
    },
    previewWarnings() {
      const w = this.preview && this.preview.warnings;
      return Array.isArray(w) ? w : [];
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.resetState();
    },
    /** Prefill the code/description from the file, but never over something the user typed. */
    parsedBundle(bundle) {
      const src = (bundle && bundle.source) || {};
      if (!this.codeTouched) {
        this.versionCode = this.normalizeCode(src.questionVersionCode || this.suggestedCode || '');
      }
      if (!this.descriptionTouched) {
        this.description = src.description || '';
      }
    },
  },
  methods: {
    normalizeCode(value) {
      return String(value || '').replace(/\s+/g, '').toUpperCase();
    },
    resetState() {
      this.importText = '';
      this.fileName = '';
      this.parseError = '';
      this.error = '';
      this.preview = null;
      this.versionCode = this.normalizeCode(this.suggestedCode);
      this.description = '';
      this.codeTouched = false;
      this.descriptionTouched = false;
      this.checking = false;
      this.importing = false;
      const el = this.$refs.importFile;
      if (el) el.value = '';
    },
    close() {
      this.$emit('update:modelValue', false);
    },
    pickImportFile() {
      const el = this.$refs.importFile;
      // Clearing FIRST so re-picking the SAME file still fires `change` (the browser skips the
      // event when the value is unchanged).
      if (el) { el.value = ''; el.click(); }
    },
    onImportFileChosen(evt) {
      const file = evt && evt.target && evt.target.files && evt.target.files[0];
      if (!file) return;
      this.fileName = file.name || '';
      this.error = '';
      this.preview = null;
      const reader = new FileReader();
      reader.onload = () => { this.importText = String(reader.result || ''); };
      reader.onerror = () => {
        this.importText = '';
        this.parseError = 'อ่านไฟล์ไม่สำเร็จ';
      };
      reader.readAsText(file);
    },
    /** Any edit invalidates the dry run — the preview must describe the text that is there NOW. */
    onTextInput() {
      this.preview = null;
      this.error = '';
    },
    onCodeInput(value) {
      this.codeTouched = true;
      this.versionCode = this.normalizeCode(value);
      // The code is part of what the dry run decided (collision → `Q3-2`), so re-check is required.
      this.preview = null;
    },
    /** Shared payload of both calls — the only difference between them is `dryRun`. */
    buildRequest(dryRun) {
      return {
        bundle: this.parsedBundle,
        taxConfigVersionId: this.configVersionId,
        versionCode: this.normalizeCode(this.versionCode) || null,
        description: (this.description || '').trim() || null,
        dryRun,
      };
    },
    async runDryRun() {
      if (!this.parsedBundle || this.checking || this.importing) return;
      if (!(Number(this.configVersionId) > 0)) {
        this.error = 'ไม่รู้ว่าจะนำเข้าลงคอนฟิกไหน — ปิดหน้าต่างแล้วกดปุ่มนำเข้าใหม่อีกครั้ง';
        return;
      }
      this.checking = true;
      this.error = '';
      this.preview = null;
      try {
        this.preview = await taxQuestionV2Api.importBundle(this.buildRequest(true));
      } catch (e) {
        this.error = (e && e.message) || 'ตรวจไฟล์ไม่สำเร็จ';
      } finally {
        this.checking = false;
      }
    },
    async runImport() {
      if (!this.preview || this.checking || this.importing) return;
      this.importing = true;
      this.error = '';
      try {
        const result = await taxQuestionV2Api.importBundle(this.buildRequest(false));
        // Deliberately NOT closing here — the parent reloads the list, shows the warnings and
        // decides when this dialog goes away.
        this.$emit('imported', result);
      } catch (e) {
        this.error = (e && e.message) || 'นำเข้าชุดคำถามไม่สำเร็จ';
      } finally {
        this.importing = false;
      }
    },
  },
};
</script>

<style scoped>
.text-white-50 {
  opacity: 0.8;
}
.cfgv2-qimport-warn-list {
  margin: 0;
  padding-left: 18px;
}
</style>
